import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './SignUpModal.css';
import {
  trackSignUpModalOpened,
  trackSignUpModalClosed,
  trackSignUpSuccess,
  trackSignUpError,
  trackSignUpSuccessViewed,
  trackPrivacyPolicyClick
} from '../utils/analytics';

function SignUpModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Track modal open/close
  useEffect(() => {
    if (isOpen) {
      trackSignUpModalOpened();
    }
  }, [isOpen]);

  // Track when success message is displayed
  useEffect(() => {
    if (isSuccess) {
      trackSignUpSuccessViewed();
    }
  }, [isSuccess]);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsValid(validateEmail(value));
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValid) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    // Timeout controller - abort request after 10 seconds
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      // Get backend URL from environment variable
      // In production, this comes from .env.production
      // In development, this comes from .env or defaults to localhost
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const requestUrl = `${apiUrl}/api/signup`;

      // Log request in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[SignUp] Sending request to:', requestUrl);
        console.log('[SignUp] Email:', email.substring(0, 3) + '***');
      }

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name: name.trim() || undefined,
          company: company.trim() || undefined,
          title: title.trim() || undefined
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      // Log response in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[SignUp] Response status:', response.status);
        console.log('[SignUp] Response data:', data);
      }

      // Success cases
      if (response.ok || response.status === 200) {
        setIsSuccess(true);
        setEmail('');
        setName('');
        setCompany('');
        setTitle('');
        trackSignUpSuccess();

        // Show warning if email notification failed (207 status)
        if (response.status === 207 || data.warning) {
          console.warn('[SignUp] Partial success:', data.warning);
          // You could show this warning to the user if needed
        }
      }
      // Client errors (400-499)
      else if (response.status >= 400 && response.status < 500) {
        const errorMsg = data.error || data.message || 'Please check your input and try again';
        setErrorMessage(errorMsg);
        trackSignUpError(`Validation Error: ${data.code || 'Unknown'}`);

        if (process.env.NODE_ENV === 'development') {
          console.error('[SignUp] Validation error:', data.code, errorMsg);
        }
      }
      // Server errors (500+)
      else if (response.status >= 500) {
        setErrorMessage('Server error. Please try again in a few moments.');
        trackSignUpError(`Server Error: ${response.status}`);

        if (process.env.NODE_ENV === 'development') {
          console.error('[SignUp] Server error:', data.code, data.error);
        }
      }
      // Unexpected status codes
      else {
        setErrorMessage('Unexpected error. Please try again.');
        trackSignUpError(`Unexpected Status: ${response.status}`);

        if (process.env.NODE_ENV === 'development') {
          console.error('[SignUp] Unexpected status:', response.status, data);
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle specific error types
      if (error.name === 'AbortError') {
        setErrorMessage('Request timed out. Please check your connection and try again.');
        trackSignUpError('Request Timeout');
        if (process.env.NODE_ENV === 'development') {
          console.error('[SignUp] Request timeout');
        }
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setErrorMessage('Cannot connect to server. Please check your internet connection.');
        trackSignUpError('Network Error');
        if (process.env.NODE_ENV === 'development') {
          console.error('[SignUp] Network error:', error.message);
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
        trackSignUpError(`Unexpected Error: ${error.name || 'Unknown'}`);
        if (process.env.NODE_ENV === 'development') {
          console.error('[SignUp] Unexpected error:', error);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setName('');
    setCompany('');
    setTitle('');
    setIsValid(false);
    setIsSuccess(false);
    setErrorMessage('');
    trackSignUpModalClosed();
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <button className="modal-close" onClick={handleClose}>
          ×
        </button>

        {!isSuccess ? (
          <>
            <h2 className="modal-title">Sign Up for Updates</h2>
            <p className="modal-description">
              Want to learn more about our AI compliance framework and how it can help your organization?
            </p>

            <form onSubmit={handleSubmit} className="signup-form">
              <div className="form-group">
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Email address *"
                  className={`email-input ${errorMessage ? 'error' : ''}`}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="form-group">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="email-input"
                  disabled={isLoading}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Company (optional)"
                  className="email-input"
                  disabled={isLoading}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Job Title (optional)"
                  className="email-input"
                  disabled={isLoading}
                  maxLength={100}
                />
              </div>

              {errorMessage && (
                <p className="error-message">{errorMessage}</p>
              )}

              <button
                type="submit"
                className="submit-button"
                disabled={!isValid || isLoading}
              >
                {isLoading ? 'Signing up...' : 'Sign Up'}
              </button>
            </form>

            <div className="privacy-notice">
              <p className="privacy-text">
                We won't spam you or share your email with anyone external.
              </p>
              <Link
                to="/privacy-policy"
                className="privacy-link"
                onClick={() => {
                  trackPrivacyPolicyClick();
                  handleClose();
                }}
              >
                Read our Privacy Policy
              </Link>
            </div>
          </>
        ) : (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2 className="success-title">Thank you for signing up!</h2>
            <p className="success-text">
              We'll share more details about our AI compliance framework soon.
            </p>
            <button className="success-button" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignUpModal;
